/**
 * core/intelligence/priorityEngine.ts
 *
 * Predictive Task Prioritisation — scores monday.com items so the Focus
 * Suggestion widget can surface the most urgent work first.
 *
 * Scoring factors:
 *   • Priority label  (Critical / High / Medium / Low)
 *   • Days overdue    (linear multiplier)
 *   • Days until due  (urgency curve)
 *   • Status modifier (Blocked / Stuck / Escalated / In Progress / Done)
 *
 * The result is a ScoredItem[]  sorted descending by score, ready to render.
 */

import {
  PRIORITY_WEIGHTS,
  OVERDUE_MULTIPLIER,
  STATUS_MODIFIERS,
} from '@utils/defaults';
import type { MondayItem, ScoredItem, Priority } from '@core/types';

// ─── Helpers ──────────────────────────────────────────────────────────────────

const MS_PER_DAY = 1_000 * 60 * 60 * 24;

function daysDelta(isoDate: string | undefined, now: Date): number | null {
  if (!isoDate) return null;
  const target = new Date(isoDate);
  if (isNaN(target.getTime())) return null;
  return (target.getTime() - now.getTime()) / MS_PER_DAY; // negative = overdue
}

function getPriorityScore(priorityLabel: string | undefined): number {
  if (!priorityLabel) return PRIORITY_WEIGHTS.Low;
  const norm = priorityLabel.trim() as Priority;
  return PRIORITY_WEIGHTS[norm] ?? PRIORITY_WEIGHTS.Low;
}

function getStatusScore(statusLabel: string | undefined): number {
  if (!statusLabel) return 0;
  return STATUS_MODIFIERS[statusLabel] ?? 0;
}

/**
 * Urgency bonus for items that are due within the next 7 days.
 * Curve: 0 at 7+ days → 30 at 1 day → 50 at same-day.
 */
function dueSoonBonus(daysUntilDue: number): number {
  if (daysUntilDue <= 0) return 0; // already overdue — handled separately
  if (daysUntilDue >= 7) return 0;
  return Math.round(30 * (1 - (daysUntilDue - 1) / 6)); // linear 30→0
}

// ─── Main export ──────────────────────────────────────────────────────────────

export interface PriorityEngineOptions {
  /** Maximum items to return (default: 10) */
  limit?: number;
  /** Exclude items whose status score is <= -999 (i.e. "Done") */
  excludeDone?: boolean;
}

/**
 * Score and rank a list of monday.com items.
 *
 * @param items  - Raw MondayItem[] from the API
 * @param now    - Reference date (defaults to current time; injectable for tests)
 * @param opts   - Optional tuning parameters
 */
export function scoreItems(
  items: MondayItem[],
  now: Date = new Date(),
  opts: PriorityEngineOptions = {},
): ScoredItem[] {
  const { limit = 10, excludeDone = true } = opts;

  const scored: ScoredItem[] = items.map((item) => {
    const priorityLabel = item.columnValues?.priority;
    const statusLabel = item.columnValues?.status;
    const dueDateStr = item.columnValues?.due_date;

    const priorityScore = getPriorityScore(priorityLabel);
    const statusScore = getStatusScore(statusLabel);
    const deltaDays = daysDelta(dueDateStr, now);

    let overdueBonus = 0;
    let urgencyBonus = 0;

    if (deltaDays !== null) {
      if (deltaDays < 0) {
        // Overdue: positive contribution proportional to days overdue
        overdueBonus = Math.round(Math.abs(deltaDays) * OVERDUE_MULTIPLIER);
      } else {
        urgencyBonus = dueSoonBonus(deltaDays);
      }
    }

    const totalScore = priorityScore + statusScore + overdueBonus + urgencyBonus;

    return {
      item,
      score: totalScore,
      reasons: buildReasons({ priorityLabel, statusLabel, deltaDays }),
    };
  });

  return scored
    .filter((s) => {
      if (!excludeDone) return true;
      const statusScore = getStatusScore(s.item.columnValues?.status);
      return statusScore > -999;
    })
    .sort((a, b) => b.score - a.score)
    .slice(0, limit);
}

// ─── Human-readable reasons ───────────────────────────────────────────────────

function buildReasons(params: {
  priorityLabel: string | undefined;
  statusLabel: string | undefined;
  deltaDays: number | null;
}): string[] {
  const { priorityLabel, statusLabel, deltaDays } = params;
  const reasons: string[] = [];

  if (priorityLabel === 'Critical' || priorityLabel === 'High') {
    reasons.push(`${priorityLabel} priority`);
  }

  if (statusLabel === 'Blocked' || statusLabel === 'Stuck' || statusLabel === 'Escalated') {
    reasons.push(statusLabel);
  }

  if (deltaDays !== null) {
    if (deltaDays < 0) {
      const days = Math.round(Math.abs(deltaDays));
      reasons.push(`Overdue by ${days} day${days !== 1 ? 's' : ''}`);
    } else if (deltaDays < 1) {
      reasons.push('Due today');
    } else if (deltaDays <= 3) {
      reasons.push(`Due in ${Math.ceil(deltaDays)} day${Math.ceil(deltaDays) !== 1 ? 's' : ''}`);
    }
  }

  return reasons;
}
